import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFriends } from '../../context/FriendContext';
import { UserCircle, Menu, X, LogOut, LogIn, PlusCircle, Home, BarChart, HelpCircle, Ship, Users, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import CustomCruiseIcon from '../CustomCruiseIcon';
import NotificationBadge from '../NotificationBadge';
import NotificationsDropdown from '../NotificationsDropdown';
import { Notification } from '../../types/friends';
import { SHOW_FRIENDS_BUTTON, SHOW_NOTIFICATION_BUTTON } from '../../config';
import { debug } from '../../utils/debug';

import AcceptCruiseInviteModal from '../AcceptCruiseInviteModal';

// Custom breakpoint to prevent navbar content wrapping
const CUSTOM_NAVBAR_BREAKPOINT = 1280;

const NAVBAR_NAME_MAX_LENGTH_DESKTOP = 14;
const NAVBAR_NAME_MAX_LENGTH_MOBILE = 26;

const truncateNameForNavbar = (name: string, maxLength: number): string =>
  name.length > maxLength ? name.slice(0, maxLength) + '...' : name;

const Navbar = () => {
  const { currentUser, logout, preferredDisplayName, loading, isAdmin } = useAuth();
  const { notifications, markNotificationRead, markAllNotificationsRead, deleteNotification } = useFriends();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= CUSTOM_NAVBAR_BREAKPOINT : false
  );
  const [showNotifications, setShowNotifications] = useState(false);
  const [inviteModalNotification, setInviteModalNotification] = useState<Notification | null>(null);
  const unreadNotificationCount = useMemo(() => {
    return notifications.filter((n) => !n.read).length;
  }, [notifications]);

  const handleNotificationClick = (notification: Notification) => {
    debug.log('[Navbar] handleNotificationClick called', {
      notificationId: notification.id,
      type: notification.type,
      relatedRequestId: notification.relatedRequestId,
      cruiseId: (notification as any).cruiseId,
      fullNotification: notification,
    });
    setShowNotifications(false);

    if (notification.type === 'cruise-invite') {
      setInviteModalNotification(notification);
      return;
    }

    // Determine target path based on notification type
    let targetPath = '/friends';
    let state: any = undefined;

    const n = notification; // cruiseId is already typed on Notification
    if (notification.type === 'friend-request' || (notification.type as string) === 'friend-accepted') {
      targetPath = '/friends';
      state = notification.type === 'friend-request' ? { openRequestId: notification.relatedRequestId } : undefined;
    } else if (notification.type === 'itinerary-update' && n.cruiseId) {
      targetPath = `/cruise/${n.cruiseId}`;
    } else if (notification.type === 'itinerary-available' && n.cruiseId) {
      targetPath = `/edit-cruise/${n.cruiseId}`;
    }

    debug.log('[Navbar] scheduling navigate', { targetPath, state });
    setTimeout(() => {
      debug.log('[Navbar] setTimeout fired, calling navigate', { targetPath, state });
      try {
        navigate(targetPath, state != null ? { state } : undefined);
        debug.log('[Navbar] navigate() called successfully');
      } catch (err) {
        debug.error('[Navbar] navigate() threw', err);
      }
    }, 0);
  };


  // Monitor window size to determine layout
  useEffect(() => {
    const handleResize = () => {
      setIsDesktopLayout(window.innerWidth >= CUSTOM_NAVBAR_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout', error);
    }
  };

  // Memoize display name calculation to prevent unnecessary recalculations and name flipping
  // Show nothing until loading completes AND preferredDisplayName has been checked (not undefined)
  // This prevents showing currentUser.displayName before we know if preferredDisplayName exists
  const displayName = useMemo(() => {
    const state = {
      loading,
      hasCurrentUser: !!currentUser,
      currentUserDisplayName: currentUser?.displayName,
      currentUserEmail: currentUser?.email,
      preferredDisplayName,
      preferredDisplayNameType: typeof preferredDisplayName,
      preferredDisplayNameIsUndefined: preferredDisplayName === undefined,
      preferredDisplayNameIsNull: preferredDisplayName === null,
      preferredDisplayNameIsString: typeof preferredDisplayName === 'string'
    };
    
    debug.log('[Navbar] displayName calculation', state);
    
    // CRITICAL: Don't show anything until preferredDisplayName is explicitly set (not undefined)
    // undefined = not checked yet, null = checked but doesn't exist, string = checked and exists
    // We must wait for preferredDisplayName to be set before showing any name to prevent flickering
    if (preferredDisplayName === undefined) {
      debug.log('[Navbar] preferredDisplayName is undefined, returning empty string');
      return '';
    }
    
    // Now that we know preferredDisplayName has been checked:
    // 1. If it's a string, use it
    // 2. If it's null (checked but doesn't exist), fall back to displayName
    // 3. Also ensure loading is false and currentUser exists as safety checks
    if (!loading && currentUser) {
      if (preferredDisplayName !== null && preferredDisplayName !== undefined) {
        debug.log('[Navbar] Using preferredDisplayName:', preferredDisplayName);
        return preferredDisplayName;
      }
      // preferredDisplayName is null (checked but doesn't exist), use displayName
      const fallbackName = currentUser.displayName || currentUser.email || '';
      debug.log('[Navbar] preferredDisplayName is null, using fallback:', fallbackName);
      return fallbackName;
    }
    
    // Still loading or no user, show nothing
    debug.log('[Navbar] Still loading or no user, returning empty string');
    return '';
  }, [loading, currentUser, preferredDisplayName]);

  // Helper function to determine if a link is active
  const isActiveLink = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname === path;
  };

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50 bg-primary-600 text-white shadow-lg min-h-[var(--full-header-height)] flex items-center"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Fixed header content height (matches --nav-height / 64px) */}
      <div className="flex items-center w-full mobile-navbar-safe lg:pt-0">
        <div className="container mx-auto px-4 w-full">
          <div className="flex justify-between items-center">
            <Link
              to="/"
              className="flex items-center space-x-2 text-xl font-bold rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label="Cruising Along Home"
            >
              <CustomCruiseIcon size={48} className="text-accent" />
              <span className="text-white leading-none">Cruising Along</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className={isDesktopLayout ? 'flex items-center space-x-6' : 'hidden'}>
              {currentUser ? (
                <>
                  <Link to="/dashboard" className={`${isActiveLink('/dashboard') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200 flex items-center gap-1`}>
                    <Home className="h-4 w-4 align-middle" />
                    <span>Dashboard</span>
                  </Link>
                  <Link to="/highlights" className={`${isActiveLink('/highlights') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200 flex items-center gap-1`}>
                    <BarChart className="h-4 w-4 align-middle" />
                    <span>Highlights</span>
                  </Link>
                  <Link to="/collection" className={`${isActiveLink('/collection') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200 flex items-center gap-1`}>
                    <Ship className="h-4 w-4 align-middle" />
                    <span>Collection</span>
                  </Link>
                  {SHOW_FRIENDS_BUTTON && (
                    <Link to="/friends" className={`${isActiveLink('/friends') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200 flex items-center gap-1`}>
                      <Users className="h-4 w-4 align-middle" />
                      <span>Friends</span>
                    </Link>
                  )}
                  {isAdmin && (
                    <Link to="/admin" className={`${isActiveLink('/admin') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200 flex items-center gap-1`}>
                      <ShieldCheck className="h-4 w-4 align-middle" />
                      <span>Admin</span>
                    </Link>
                  )}
                  <Link to="/add-cruise" className={`${isActiveLink('/add-cruise') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200 flex items-center gap-1`}>
                    <PlusCircle className="h-4 w-4 align-middle" />
                    <span>Add Cruise</span>
                  </Link>
                  <Link to="/help" className={`${isActiveLink('/help') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200 flex items-center gap-1`}>
                    <HelpCircle className="h-4 w-4 align-middle" />
                    <span>Help</span>
                  </Link>
                  <div className="pl-4 border-l border-white/20 flex items-center space-x-3">
                    {currentUser && SHOW_NOTIFICATION_BUTTON && isDesktopLayout && (
                      <div className="relative">
                        <NotificationBadge
                          count={unreadNotificationCount}
                          onClick={() => setShowNotifications(!showNotifications)}
                        />
                        <NotificationsDropdown
                          notifications={notifications}
                          isOpen={showNotifications}
                          onClose={() => setShowNotifications(false)}
                          onMarkRead={markNotificationRead}
                          onMarkAllRead={markAllNotificationsRead}
                          onDelete={deleteNotification}
                          onNotificationClick={handleNotificationClick}
                        />
                      </div>
                    )}
                    <Link to="/profile" className={`flex items-center space-x-2 ${isActiveLink('/profile') ? 'text-accent' : 'text-gray-100'} hover:text-accent transition duration-200`}>
                      <UserCircle className="h-4 w-4 align-middle" />
                      {displayName && <span>{truncateNameForNavbar(displayName, NAVBAR_NAME_MAX_LENGTH_DESKTOP)}</span>}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 hover:bg-secondary-500 text-white rounded-md transition duration-200 font-medium"
                    >
                      <LogOut className="h-4 w-4 mr-1 align-middle" />
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center px-4 py-2 text-sm bg-secondary-600 hover:bg-secondary-500 text-white rounded-md transition duration-200 font-medium"
                  >
                    <LogIn className="h-4 w-4 mr-1 align-middle" />
                    Log In
                  </Link>
                  <Link
                    to="/signup"
                    className="px-4 py-2 bg-accent hover:bg-accent-400 text-white rounded-md transition duration-200"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </nav>

            {/* Mobile header actions (notification button + hamburger menu) */}
            <div className={isDesktopLayout ? 'hidden' : 'flex items-center space-x-3'}>
              {currentUser && SHOW_NOTIFICATION_BUTTON && !isDesktopLayout && (
                <div className="relative">
                  <NotificationBadge
                    count={unreadNotificationCount}
                    onClick={() => setShowNotifications(!showNotifications)}
                  />
                  <NotificationsDropdown
                    notifications={notifications}
                    isOpen={showNotifications}
                    onClose={() => setShowNotifications(false)}
                    onMarkRead={markNotificationRead}
                    onMarkAllRead={markAllNotificationsRead}
                    onDelete={deleteNotification}
                    onNotificationClick={handleNotificationClick}
                  />
                </div>
              )}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <motion.div
          className={isDesktopLayout ? 'hidden' : 'fixed top-[calc(var(--full-header-height)+var(--navbar-bump))] left-0 right-0 bg-primary-600 z-40 overflow-y-auto h-[calc(100vh-(var(--full-header-height)+var(--navbar-bump)))] py-4 border-t border-white/20'}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          <nav className="flex flex-col space-y-4 px-4">
            {currentUser ? (
              <>
                <Link to="/dashboard" className={`flex items-center space-x-2 ${isActiveLink('/dashboard') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                  <Home className="h-5 w-5 align-middle" />
                  <span>Dashboard</span>
                </Link>
                <Link to="/highlights" className={`flex items-center space-x-2 ${isActiveLink('/highlights') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                  <BarChart className="h-5 w-5 align-middle" />
                  <span>Highlights</span>
                </Link>
                <Link to="/collection" className={`flex items-center space-x-2 ${isActiveLink('/collection') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                  <Ship className="h-5 w-5 align-middle" />
                  <span>Collection</span>
                </Link>
                {SHOW_FRIENDS_BUTTON && (
                  <Link to="/friends" className={`flex items-center space-x-2 ${isActiveLink('/friends') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                    <Users className="h-5 w-5 align-middle" />
                    <span>Friends</span>
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin" className={`flex items-center space-x-2 ${isActiveLink('/admin') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                    <ShieldCheck className="h-5 w-5 align-middle" />
                    <span>Admin</span>
                  </Link>
                )}
                <Link to="/add-cruise" className={`flex items-center space-x-2 ${isActiveLink('/add-cruise') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                  <PlusCircle className="h-5 w-5 align-middle" />
                  <span>Add Cruise</span>
                </Link>
                <Link to="/help" className={`flex items-center space-x-2 ${isActiveLink('/help') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                  <HelpCircle className="h-5 w-5 align-middle" />
                  <span>Help</span>
                </Link>
                <div className="pt-4 border-t border-white/20 flex flex-col space-y-4">
                  <Link to="/profile" className={`flex items-center space-x-2 ${isActiveLink('/profile') ? 'text-accent' : 'text-white'} hover:text-accent transition duration-200`} onClick={() => setIsMenuOpen(false)}>
                    <UserCircle className="h-5 w-5 align-middle" />
                    {displayName && <span>{truncateNameForNavbar(displayName, NAVBAR_NAME_MAX_LENGTH_MOBILE)}</span>}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-2 text-white bg-secondary-600 hover:bg-secondary-500 px-4 py-2 rounded-md transition duration-200 w-full font-medium"
                  >
                    <LogOut className="h-5 w-5 align-middle" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="flex items-center space-x-2 text-white bg-secondary-600 hover:bg-secondary-500 px-4 py-2 rounded-md transition duration-200 font-medium" onClick={() => setIsMenuOpen(false)}>
                  <LogIn className="h-5 w-5 align-middle" />
                  <span>Log In</span>
                </Link>
                <Link to="/signup" className="bg-accent hover:bg-accent-400 text-white px-4 py-2 rounded-md transition duration-200 inline-block" onClick={() => setIsMenuOpen(false)}>
                  Sign Up
                </Link>
              </>
            )}
          </nav>
        </motion.div>
      )}

      {inviteModalNotification && (
        <AcceptCruiseInviteModal
          isOpen={!!inviteModalNotification}
          notification={inviteModalNotification}
          onClose={() => setInviteModalNotification(null)}
          onAccepted={(newCruiseId) => {
            navigate(`/cruise/${newCruiseId}`);
          }}
        />
      )}
    </motion.header>
  );
};

export default Navbar;
